import { useLayers } from '@ais-ui/react-openlayers';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useState } from 'react';
import { GridJobInstance, useGPSLost } from './GPSLostContext';

interface UseGPSLostAddGridToMapResult {
  addGridToMap: (
    configuration: any,
    instance: GridJobInstance,
    zoom?: boolean
  ) => Promise<void>;
  isAdding: boolean;
}

export const useGPSLostAddGridToMap = (): UseGPSLostAddGridToMapResult => {
  const { addLayer, getLayersByIdContaining, zoomToLayer, removeLayer } =
    useLayers();
  const { toast } = useToast();
  const { messages } = useLocalization();
  const [isAdding, setIsAdding] = useState(false);
  const { createGridLayerAndSetSelectedInstance } = useGPSLost();

  // Function to check if an instance can be added to the map
  const canAddInstance = (instance: GridJobInstance | undefined): boolean => {
    if (!instance) return false;
    return instance.status === 'success' || instance.status === 'no_results';
  };

  const addGridToMap = async (
    configuration: any,
    instance: GridJobInstance,
    zoom = true
  ) => {
    if (!instance || !canAddInstance(instance)) {
      console.warn(
        'Cannot add instance to map - invalid or unsuccessful instance'
      );
      toast({
        className: 'gps-lost-grid-invalid-grid-instance',
        title: messages.genericToastTitleWarning,
        description: `Invalid or unsuccessful instance: ${
          instance?.status || 'unknown'
        }`,
        variant: 'warning',
      });
      return;
    }

    const gpsLostGridConfigurationIdPart = `gps_lost_grid_${configuration.id}`;
    const gpsLostGridId = `${gpsLostGridConfigurationIdPart}_${instance.id}`;
    setIsAdding(true);

    try {
      const { layer, maxGpsLostValue } =
        await createGridLayerAndSetSelectedInstance(
          configuration,
          instance,
          gpsLostGridId
        );

      // remove existing layers from this configuration
      const oldGPSLostGridLayers = getLayersByIdContaining(
        gpsLostGridConfigurationIdPart
      );
      oldGPSLostGridLayers.forEach((layer) => {
        removeLayer(layer.layer);
      });

      const oldGPSLostTrackLayers = getLayersByIdContaining('gps-lost-track');
      oldGPSLostTrackLayers.forEach((layer) => {
        removeLayer(layer.layer);
      });

      addLayer(
        layer,
        `GPS Lost - ${configuration.job_name}`,
        true,
        true,
        false,
        {
          type: 'gps-lost',
          maxGpsLostValue,
        },
        gpsLostGridId,
        false,
        true,
        false
      );

      if (zoom) {
        zoomToLayer(layer);
      }

      toast({
        title: messages.genericToastTitleSuccess,
        description: 'GPS Lost Grid has been added to the map',
      });
    } catch (error) {
      console.error('Error fetching instance data:', error);
      toast({
        title: messages.genericToastTitleError,
        description: 'Error adding GPS Lost Grid to the map',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  return {
    addGridToMap,
    isAdding,
  };
};
